/**
 * Thin lines of light set into the floor that guide visitors without signs: from the
 * arrival arch to the reception, around the reception's stations, from the reception round
 * the sculpture's dais, on to the Study Gateway, and out to the two lounges. One ribbon
 * mesh, one draw call. Each segment belongs to a group whose brightness the scene sets as
 * the visitor's journey reaches it; pulses travel along every line, faster on lit groups.
 */
import * as THREE from 'three';
import { TOKENS } from '../../ui/theme.ts';
import type { TimeUniform } from './glow.ts';

export const PATH_GROUPS = ['arrival', 'reception', 'sculpture', 'gateway', 'lounges'] as const;
export type PathGroup = (typeof PATH_GROUPS)[number];

export interface Pathways {
  mesh: THREE.Mesh;
  /** 0 dim .. 1 fully lit, per group; the shader eases toward it. */
  setLevel(group: PathGroup, level: number): void;
  /** Colour of the gateway group (the selected study's colour, or cyan). */
  setGatewayColour(colour: string | undefined): void;
  update(dt: number): void;
}

type P = [number, number];

function arc(cx: number, cz: number, r: number, fromDeg: number, toDeg: number, steps = 48): P[] {
  const out: P[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = ((fromDeg + ((toDeg - fromDeg) * i) / steps) * Math.PI) / 180;
    out.push([cx + Math.sin(a) * r, cz - Math.cos(a) * r]);
  }
  return out;
}

function bezier(a: P, c: P, b: P, steps = 32): P[] {
  const out: P[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    out.push([
      u * u * a[0] + 2 * u * t * c[0] + t * t * b[0],
      u * u * a[1] + 2 * u * t * c[1] + t * t * b[1],
    ]);
  }
  return out;
}

/** The lines, each with its group. Angles in degrees from north, as in the hall. */
export function pathLines(): { group: PathGroup; points: P[] }[] {
  const lines: { group: PathGroup; points: P[] }[] = [];
  // Arrival: two lines from the arch converging on the reception.
  for (const s of [-1, 1])
    lines.push({
      group: 'arrival',
      points: bezier([s * 1.6, 18.6], [s * 1.4, 16], [s * 0.45, 14.6]),
    });
  // Reception: an arc behind the stations, around the arrival point.
  lines.push({ group: 'reception', points: arc(0, 13.5, 3.6, -88, 88, 64) });
  lines.push({ group: 'reception', points: arc(0, 13.5, 3.75, -88, 88, 64) });
  // From the reception round the dais.
  for (const s of [-1, 1])
    lines.push({
      group: 'sculpture',
      points: bezier([s * 0.45, 9.9], [s * 0.6, 7], [s * 2.6, 3.6]),
    });
  lines.push({ group: 'sculpture', points: arc(0, 0, 4.4, 0, 360, 120) });
  // On to the gateway: two lines converging on the portal.
  for (const s of [-1, 1])
    lines.push({
      group: 'gateway',
      points: bezier([s * 2.6, -3.6], [s * 1.2, -9], [s * 0.9, -13.4]),
    });
  lines.push({ group: 'gateway', points: arc(0, -14.6, 2.6, -120, 120, 48) });
  // Lounges east and west.
  for (const s of [-1, 1]) {
    lines.push({
      group: 'lounges',
      points: bezier([s * 4.3, 1.0], [s * 7.5, 0.2], [s * 9.6, 1.0]),
    });
    lines.push({ group: 'lounges', points: arc(s * 12, 1, 2.7, 0, 360, 64) });
  }
  return lines;
}

export function buildPathways(time: TimeUniform): Pathways {
  const width = 0.045;
  const positions: number[] = [];
  const dist: number[] = [];
  const group: number[] = [];
  const index: number[] = [];
  for (const line of pathLines()) {
    const g = PATH_GROUPS.indexOf(line.group);
    let along = 0;
    const base = positions.length / 3;
    line.points.forEach((p, i) => {
      const prev = line.points[Math.max(0, i - 1)]!;
      const next = line.points[Math.min(line.points.length - 1, i + 1)]!;
      if (i > 0) along += Math.hypot(p[0] - prev[0], p[1] - prev[1]);
      const tx = next[0] - prev[0];
      const tz = next[1] - prev[1];
      const len = Math.hypot(tx, tz) || 1;
      const nx = (-tz / len) * (width / 2);
      const nz = (tx / len) * (width / 2);
      positions.push(p[0] + nx, 0.006, p[1] + nz, p[0] - nx, 0.006, p[1] - nz);
      dist.push(along, along);
      group.push(g, g);
      if (i > 0) {
        const a = base + (i - 1) * 2;
        index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    });
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aDist', new THREE.Float32BufferAttribute(dist, 1));
  geometry.setAttribute('aGroup', new THREE.Float32BufferAttribute(group, 1));
  geometry.setIndex(index);
  const levels = new Float32Array(PATH_GROUPS.length).fill(0.2);
  const targets = new Float32Array(PATH_GROUPS.length).fill(0.2);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: time,
      uLevel: { value: levels },
      uCyan: { value: new THREE.Color(TOKENS.cyan) },
      uGate: { value: new THREE.Color(TOKENS.cyan) },
    },
    vertexShader: /* glsl */ `
      attribute float aDist;
      attribute float aGroup;
      varying float vDist;
      varying float vGroup;
      void main() {
        vDist = aDist;
        vGroup = aGroup;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uLevel[${PATH_GROUPS.length}];
      uniform vec3 uCyan;
      uniform vec3 uGate;
      varying float vDist;
      varying float vGroup;
      void main() {
        int g = int(vGroup + 0.5);
        float level = 0.0;
        for (int i = 0; i < ${PATH_GROUPS.length}; i++) if (i == g) level = uLevel[i];
        float speed = 0.9 + level * 2.2;
        float x = fract((vDist - uTime * speed) / 3.2);
        float pulse = smoothstep(0.0, 0.03, x) * pow(1.0 - x, 14.0);
        vec3 colour = g == 3 ? uGate : uCyan;
        float a = 0.16 + level * 0.6 + pulse * (0.35 + level * 1.2);
        vec3 c = mix(colour, vec3(1.0), pulse * 0.6) * a;
        gl_FragColor = vec4(c, a);
      }`,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'lobby-pathways';
  mesh.renderOrder = 3;
  const gate = new THREE.Color(TOKENS.cyan);
  return {
    mesh,
    setLevel: (g, level) => {
      targets[PATH_GROUPS.indexOf(g)] = THREE.MathUtils.clamp(level, 0, 1);
    },
    setGatewayColour: (c) => {
      gate.set(c ?? TOKENS.cyan);
    },
    update: (dt) => {
      for (let i = 0; i < levels.length; i++)
        levels[i] = levels[i]! + (targets[i]! - levels[i]!) * Math.min(1, dt * 2);
      (material.uniforms.uGate!.value as THREE.Color).lerp(gate, Math.min(1, dt * 2));
    },
  };
}
